// Phase 2 operational documents (03 §3.4, §3.8): customers, shipments, boxes, stock.
// All are server-written; clients read them within scope (every document carries branchId and
// sectorId for the rules) and change them only through commands (04).
import { z } from 'zod';
import { isPieceId, trackingNoProblem } from '@ecobox/domain';
import { cancellationSchema, destinationChargeSchema } from './shipmentChanges';
import { predictedWindowSchema } from './insights';
import {
  branchCodeSchema,
  numberSeriesIdSchema,
  legalAddressSchema,
  sectorCodeSchema,
  SERVICES,
  serviceLineSchema,
  sizeCodeSchema,
  slugSchema,
} from './config';

const minor = z.number().int().nonnegative().max(1e12);
const signedMinor = z.number().int().min(-1e12).max(1e12);

export const e164Schema = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, 'Phone in international format, e.g. +966501234567');
export const customerIdSchema = z.string().regex(/^cus_[0-9A-HJKMNP-TV-Z]{26}$/);
export const consigneeIdSchema = z.string().regex(/^con_[0-9A-HJKMNP-TV-Z]{26}$/);
/**
 * Any tracking number (03 §3.5): a number of a series (`RUH260042171`, `ACME-260001-UAE`) or one
 * typed by hand (`ABC123`). A–Z, 0–9 and '-', 4 to 20 long, never shaped like a piece label or
 * a box barcode (`trackingNoProblem`).
 */
export const trackingNoSchema = z
  .string()
  .regex(/^[A-Z0-9][A-Z0-9-]{2,18}[A-Z0-9]$/, 'Tracking number like RUH260042171')
  .superRefine((value, ctx) => {
    const why = trackingNoProblem(value);
    if (why) ctx.addIssue({ code: 'custom', message: why });
  });
export const barcodeSchema = z.string().regex(/^EB\d{10}$/, 'Box barcode like EB0000012344');
/** A Prime piece label: the tracking number plus `-P1`, `-P2`, … (12 §12.5, Prime pieces). */
export const pieceIdSchema = z
  .string()
  .refine((v) => isPieceId(v), 'Prime piece like DXB260000013-P1');
/** Anything a partner carries: a balikbayan box barcode or a Prime piece label. */
export const unitIdSchema = z.union([barcodeSchema, pieceIdSchema]);
export const receiptNoSchema = z.string().regex(/^R[A-Z]{3,4}\d{9}$/);

export const dimsCmSchema = z
  .object({
    l: z.number().positive().max(1000),
    w: z.number().positive().max(1000),
    h: z.number().positive().max(1000),
  })
  .strict();

export const addressSchema = z
  .object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().max(200).default(''),
    barangay: z.string().max(80).default(''),
    city: z.string().max(80).default(''),
    province: z.string().max(80).default(''),
    postalCode: z.string().max(12).default(''),
    landmark: z.string().max(200).default(''),
  })
  .strict();

// ---- Customers ---------------------------------------------------------------

export const customerPhoneSchema = z
  .object({
    e164: e164Schema,
    verified: z.boolean().default(false),
    primary: z.boolean().default(false),
  })
  .strict();

/**
 * A business customer's tax details for the invoice (06 §6.8.7). A `business` customer is B2B and
 * an `individual` is B2C; the invoice is a standard (B2B) tax invoice only when the business has a
 * VAT number or another id, plus an address. Otherwise it is a simplified invoice.
 */
export const customerTaxSchema = z
  .object({
    /** VAT number / TRN / TIN of the buyer, when registered. */
    vatNumber: z.string().trim().min(5).max(30).optional(),
    /** Another identifier when there is no VAT number (KSA: CRN, MOM, MLS, 700, SAG, NAT, GCC, IQA, PAS, OTH). */
    otherId: z
      .object({
        scheme: z.string().regex(/^[A-Z0-9]{2,5}$/, 'Scheme like CRN or NAT'),
        value: z.string().trim().min(1).max(40),
      })
      .strict()
      .optional(),
    /** The registered name in Arabic, for a KSA standard invoice. */
    nameAr: z.string().trim().max(200).optional(),
    /** Peppol participant id (UAE B2B e-invoicing). */
    peppolId: z.string().trim().max(60).optional(),
    address: legalAddressSchema.optional(),
  })
  .strict();
export type CustomerTax = z.infer<typeof customerTaxSchema>;

/** `customers/{cus_…}` */
export const customerDocSchema = z.object({
  id: customerIdSchema,
  type: z.enum(['individual', 'business']),
  name: z.string().trim().min(1).max(120),
  phones: z.array(customerPhoneSchema).min(1).max(5),
  /** Primary phone, denormalised for lookup queries. */
  phoneE164: e164Schema,
  email: z.string().max(120).default(''),
  homeBranchId: branchCodeSchema,
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  source: z
    .enum(['walk_in', 'referral', 'agent', 'website', 'app', 'social', 'other'])
    .default('walk_in'),
  tags: z.array(z.string().max(30)).max(20).default([]),
  notes: z.string().max(1000).default(''),
  /** Tax details of a business customer (B2B invoices). */
  tax: customerTaxSchema.optional(),
  /** The name in a form a duplicate check compares (`customerNameKey`): set on new customers. */
  nameKey: z.string().max(120).optional(),
  /** Set when this record was merged into another (`mergeCustomers`): that customer's id. */
  mergedInto: customerIdSchema.optional(),
  mergedAt: z.string().optional(),
  /** The Firebase Auth uid of the customer app login for this phone (Phase 6, 07 §7.1). */
  authUid: z.string().optional(),
  /** This customer's own referral code, and the friend's code they used with that friend's customer id (15 §15.2). */
  referralCode: z.string().optional(),
  referredByCode: z.string().optional(),
  referredBy: z.string().optional(),
  /**
   * How the customer was acquired (15 §15.2): set once, by the first shipment (or by the login that
   * made the profile, then confirmed by the first shipment). `referredBy` is the referrer's customer id.
   */
  acquisition: z
    .object({
      source: z.enum(['walk_in', 'referral', 'agent', 'website', 'app', 'social', 'other']),
      campaignCode: z.string().optional(),
      referredBy: z.string().optional(),
      shipmentId: z.string().optional(),
      at: z.string().optional(),
    })
    .strict()
    .optional(),
  stats: z
    .object({
      shipments: z.number().int().nonnegative(),
      firstShipmentAt: z.string().optional(),
      lastShipmentAt: z.string().optional(),
    })
    .strict(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerDoc = z.infer<typeof customerDocSchema>;

/** `customers/{id}/consignees/{con_…}`: the receiver address book. */
export const consigneeDocSchema = z.object({
  id: consigneeIdSchema,
  name: z.string().trim().min(1).max(120),
  phones: z.array(e164Schema).min(1).max(3),
  address: addressSchema,
  cityId: slugSchema,
  relationship: z.string().max(40).default(''),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
});
export type ConsigneeDoc = z.infer<typeof consigneeDocSchema>;

/** Input to the `upsertCustomer` callable (04 §4.7). */
export const upsertCustomerInputSchema = z
  .object({
    id: customerIdSchema.optional(),
    type: z.enum(['individual', 'business']).default('individual'),
    name: z.string().trim().min(1).max(120),
    phones: z.array(e164Schema).min(1).max(5),
    email: z.string().max(120).default(''),
    homeBranchId: branchCodeSchema,
    source: customerDocSchema.shape.source,
    notes: z.string().max(1000).default(''),
    tax: customerTaxSchema.optional(),
  })
  .strict();

// ---- Shipments -----------------------------------------------------------------

export const packingItemSchema = z
  .object({
    description: z.string().trim().min(1).max(120),
    qty: z.number().int().positive().max(9999),
    valueMinor: minor,
  })
  .strict();

export const invoiceLineSchema = z
  .object({
    kind: z.enum(['freight', 'addon', 'box', 'discount', 'destination_charge']),
    label: z.string().max(120),
    amountMinor: signedMinor,
    boxKey: z.string().optional(),
    cardId: z.string().optional(),
    cardVersion: z.number().int().optional(),
    ruleId: z.string().optional(),
    quantity: z.number().optional(),
    /** A box's freight line: what its price is based on, e.g. "Charged as a size M box". */
    basis: z.string().max(160).optional(),
    /** A promotion's discount line: the promotion that gave it (03 §3.3). */
    promotionId: z.string().max(60).optional(),
    /** A `destination_charge` line (`addDestinationCharge`): the destination currency, its id and its kind. */
    currency: z.string().length(3).optional(),
    chargeId: z.string().optional(),
    chargeKind: z.string().optional(),
  })
  .strict();

export const SHIPMENT_STATUSES_DOC = [
  'booked',
  'at_origin',
  'in_transit',
  'arrived',
  'out_for_delivery',
  'partially_delivered',
  'delivered',
  'returning',
  'returned',
  'cancelled',
  'lost',
] as const;

export const PAYMENT_STATUSES = ['unpaid', 'partial', 'paid', 'overpaid'] as const;

/** `shipments/{trackingNo}`: the airway bill (03 §3.4). Money is VAT-inclusive (D-18). */
export const shipmentDocSchema = z.object({
  trackingNo: trackingNoSchema,
  /**
   * How the tracking number came about (03 §3.5): `generated` from a series lease, or `typed` by
   * hand (a pre-printed agent airway bill). Absent on shipments from before numbering series.
   */
  trackingSource: z.enum(['generated', 'typed']).optional(),
  /** The numbering series a generated number came from (the branch code for the branch default). */
  seriesId: z.string().optional(),
  /** The agent who brought the shipment, when one was chosen (03 §3.5). */
  agentId: z.string().optional(),
  source: z.enum(['counter', 'pickup', 'app']),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  originBranchId: branchCodeSchema,
  /** The parent hub of a satellite origin: the shipment is listed and read there too (2026-10-30). */
  hubBranchId: branchCodeSchema.optional(),
  /** The handling mode (how it moves). Containers, legs and reserves read this. */
  service: z.enum(SERVICES),
  /** The product the customer bought (D-28). Absent on older shipments: `lineOf` derives it. */
  serviceLine: serviceLineSchema.optional(),
  shipper: z
    .object({ customerId: customerIdSchema, name: z.string(), phoneE164: e164Schema })
    .strict(),
  consignee: z
    .object({
      consigneeId: consigneeIdSchema.optional(),
      name: z.string(),
      phones: z.array(e164Schema).min(1),
      address: addressSchema,
      cityId: slugSchema,
      cityName: z.string(),
      /** Where the door is, when someone knows (a pickup, a saved address); the route planner uses it. */
      geo: z
        .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
        .strict()
        .optional(),
    })
    .strict(),
  /** The delivery door's position, when known (the same as `consignee.geo`, kept for map screens). */
  geo: z
    .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
    .strict()
    .optional(),
  destination: z
    .object({
      cityId: slugSchema,
      country: sectorCodeSchema,
      zoneId: z.string().optional(),
      routeCode: z.string().optional(),
      servingBranchId: branchCodeSchema.optional(),
      /** From coverage: delivered by EcoBox (`own`) or a partner (`partner`). */
      mode: z.enum(['own', 'partner']).optional(),
      partnerId: z.string().optional(),
    })
    .strict(),
  boxCount: z.number().int().nonnegative(),
  barcodes: z.array(unitIdSchema),
  totalWeightKg: z.number().nonnegative(),
  packingList: z.array(packingItemSchema).max(200),
  declaredValueMinor: minor,
  /** What the sale was priced with, so `amendShipment` can price it again (Phase 7). */
  addOnCodes: z.array(z.string()).max(20).optional(),
  discountMinor: minor.optional(),
  /** The promotion the sale was priced with (03 §3.3): its id, name, receipt label and discount. */
  promotion: z
    .object({
      id: z.string().max(60),
      name: z.string().max(120),
      label: z.string().max(120),
      discountMinor: minor,
    })
    .strict()
    .optional(),
  /** Destination charges booked on this shipment, in the destination currency (04 §4.6). */
  destinationAccruedMinor: minor.optional(),
  destinationCollectedMinor: minor.optional(),
  destinationCharges: z.array(destinationChargeSchema).max(30).optional(),
  /** Boxes taken off by an amendment (their barcodes are void). */
  removedBarcodes: z.array(unitIdSchema).max(200).optional(),
  cancellation: cancellationSchema.optional(),
  lastChangeAt: z.string().optional(),
  /** Written by the server (13 §13.3, `onShipmentLegs`): the expected delivery window. Never by a client. */
  predictedWindow: predictedWindowSchema.optional(),
  invoice: z
    .object({
      /** Set when the invoice is issued (06 §6.8): the tax invoice's number and its `invoices/` id. */
      number: z.string().optional(),
      invoiceId: z.string().optional(),
      currency: z.string(),
      lines: z.array(invoiceLineSchema),
      grossMinor: minor,
      netMinor: minor,
      vatMinor: minor,
      vatRatePercent: z.number(),
      version: z.number().int().positive(),
    })
    .strict(),
  money: z
    .object({
      paidMinor: minor,
      balanceMinor: signedMinor,
      status: z.enum(PAYMENT_STATUSES),
    })
    .strict(),
  status: z.enum(SHIPMENT_STATUSES_DOC),
  flags: z.object({ onHold: z.boolean(), hasException: z.boolean(), split: z.boolean() }).strict(),
  seasonTags: z.array(slugSchema).max(5).default([]),
  milestones: z.array(z.object({ key: z.string(), label: z.string(), at: z.string() }).strict()),
  publicToken: z.string().min(16),
  signaturePath: z.string().optional(),
  /** Documents attached by `attachDocument` (03 §3.4a). */
  documentCount: z.number().int().nonnegative().optional(),
  lastDocumentAt: z.string().optional(),
  collectedAt: z.string(),
  collectedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int().positive(),
});
export type ShipmentDoc = z.infer<typeof shipmentDocSchema>;

/**
 * `boxes/{barcode}`: the domain `Box` (packages/domain/src/box-types.ts) plus scope fields and
 * the size/weight recorded at collection. `branchId` = the box's current branch for the rules.
 */
export const boxDocExtraSchema = z.object({
  /** A Prime piece (`{trackingNo}-P{n}`) is a unit too: it has no pre-printed barcode (12 §12.5). */
  kind: z.enum(['box', 'piece']).default('box'),
  description: z.string().max(120).optional(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  sizeId: sizeCodeSchema,
  weightKg: z.number().nonnegative().optional(),
  dimsCm: dimsCmSchema.optional(),
  trackingNo: trackingNoSchema,
  updatedAt: z.string(),
});

// ---- Barcodes and empty-box stock ------------------------------------------

export const BARCODE_STATUSES = [
  'generated',
  'issued',
  'sold',
  'dropped',
  'attached',
  'void',
] as const;

/** `emptyBoxStock/{branchId}__{sizeId}`: changed only with atomic increments inside commands. */
export const emptyBoxStockDocSchema = z.object({
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  sizeId: sizeCodeSchema,
  onHand: z.number().int(),
  reorderLevel: z.number().int().nonnegative().default(0),
  updatedAt: z.string(),
});

/** `stockMovements/{mov_…}` */
export const stockMovementDocSchema = z.object({
  id: z.string(),
  branchId: branchCodeSchema,
  sectorId: sectorCodeSchema,
  sizeId: sizeCodeSchema,
  type: z.enum([
    'inward',
    'sale',
    'drop',
    'collection',
    'transfer_out',
    'transfer_in',
    'count_adjust',
    'damaged',
  ]),
  qty: z.number().int(),
  onHandAfter: z.number().int(),
  reference: z.string(),
  commandId: z.string(),
  at: z.string(),
  by: z.string(),
});

// ---- Number leases (02 §2.2) -------------------------------------------------

export const NUMBER_SERIES = ['tracking', 'receipt'] as const;

/** Input to the `leaseNumbers` callable: a device asks for a range while online. */
export const leaseNumbersInputSchema = z
  .object({
    branchId: branchCodeSchema,
    series: z.enum(NUMBER_SERIES),
    deviceId: z.string().regex(/^dev_[0-9A-HJKMNP-TV-Z]{26}$/),
    count: z.number().int().min(1).max(200).default(50),
    /**
     * Tracking numbers only: the numbering series (03 §3.5). Absent means the branch default, whose
     * id is the branch code, so a device from before numbering series keeps working.
     */
    seriesId: numberSeriesIdSchema.optional(),
  })
  .strict();

/** The format a tracking range was leased under (03 §3.5), copied onto the lease. */
export const seriesFormatSchema = z
  .object({
    prefix: z.string().regex(/^[A-Z0-9-]{0,8}$/),
    suffix: z.string().regex(/^[A-Z0-9-]{0,8}$/),
    digits: z.number().int().min(4).max(9),
    yearPart: z.boolean(),
    checkDigit: z.boolean(),
  })
  .strict();

/** `numberLeases/{lease_…}`, and what `leaseNumbers` returns. */
export const numberLeaseSchema = z.object({
  id: z.string(),
  series: z.enum(NUMBER_SERIES),
  branchId: branchCodeSchema,
  year: z.number().int(),
  deviceId: z.string(),
  from: z.number().int().positive(),
  to: z.number().int().positive(),
  status: z.enum(['active', 'exhausted', 'expired', 'revoked']),
  createdAt: z.string(),
  /** Tracking leases: the series and the format the numbers are made with. Absent: the branch default. */
  seriesId: z.string().optional(),
  format: seriesFormatSchema.optional(),
});
export type NumberLease = z.infer<typeof numberLeaseSchema>;
